import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Stepper,
  Step,
  StepLabel,
  Paper,
} from "@mui/material";
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  Schedule as PendingIcon,
  Person as PersonIcon,
  DateRange as DateIcon,
  MonetizationOn as MoneyIcon,
  Business as TravelIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Send as SubmitIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  AccountBalanceWallet as WalletIcon,
  CreditCard as CardIcon,
  TrendingUp as OutstandingIcon,
  TrendingDown as SettledIcon,
  ApprovalOutlined as ApprovalIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, differenceInDays, addDays } from "date-fns";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/slices/authSlice";
import { useAuth } from "../../components/features/auth/AuthGuard";
import { PERMISSIONS, TRAVEL_ACCESS_PERMISSIONS } from "../../constants";
import useNotification from "../../hooks/common/useNotification";
import useConfirmDialog from "../../hooks/common/useConfirmDialog";
import { LoadingSpinner } from "../../components/common/Loading";
import EnhancedDataTable from "../../components/features/finance/EnhancedDataTable";
import { travelAPI } from "../../services/api/travel.api";

const TravelAdvanceApprovalPage = () => {
  const user = useSelector(selectUser);
  const { hasPermission, hasAnyPermission } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { isOpen, config, openDialog, closeDialog, handleConfirm } =
    useConfirmDialog();

  // State
  const [travelAdvances, setTravelAdvances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAdvance, setSelectedAdvance] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [approvalComments, setApprovalComments] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");

  // Status configurations (backend uses: draft, approved by supervisor, approved by finance, rejected)
  const travelAdvanceStatuses = [
    { value: "draft", label: "Pending", color: "#ffc107" },
    { value: "pending", label: "Pending", color: "#ffc107" },
    { value: "submitted", label: "Pending", color: "#ffc107" },
    { value: "approved by supervisor", label: "Dept Head Approved", color: "#17a2b8" },
    { value: "department_head_approved", label: "Dept Head Approved", color: "#17a2b8" },
    { value: "approved by finance", label: "Finance Approved", color: "#28a745" },
    { value: "finance_manager_approved", label: "Finance Approved", color: "#28a745" },
    { value: "admin_approved", label: "Admin Approved", color: "#007bff" },
    { value: "rejected", label: "Rejected", color: "#dc3545" },
  ];

  // Permission-based only (no role checks): use permissions from DB, aligned with backend TRAVEL_ACCESS
  const canViewQueue = hasAnyPermission(TRAVEL_ACCESS_PERMISSIONS);
  const canApprove = hasAnyPermission(TRAVEL_ACCESS_PERMISSIONS);

  // Fetch approval queue (with timeout so page never hangs)
  const FETCH_TIMEOUT_MS = 15000;
  const fetchApprovalQueue = async () => {
    try {
      setLoading(true);
      const params = { page: 1, limit: 50, _t: Date.now() };
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("FETCH_TIMEOUT")), FETCH_TIMEOUT_MS)
      );
      const response = await Promise.race([
        travelAPI.getTravelAdvanceApprovalQueue(params),
        timeoutPromise,
      ]);
      const body = response?.data;
      // Same shape as travel request approval: { success, message, data: { travelAdvances, pagination } }
      const data = body?.data ?? body;
      const advancesData = data?.travelAdvances ?? body?.travelAdvances ?? [];
      setTravelAdvances(Array.isArray(advancesData) ? advancesData : []);
    } catch (error) {
      console.error("Error fetching travel advance approval queue:", error);
      if (error?.message === "FETCH_TIMEOUT") {
        showError("Request timed out. Please try again.");
        setTravelAdvances([]);
      } else {
        showError("Failed to fetch approval queue");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle approval
  const handleApprove = async () => {
    if (!selectedAdvance) return;

    try {
      await travelAPI.approveTravelAdvance(selectedAdvance.id, {
        comments: approvalComments,
      });
      showSuccess("Travel advance approved successfully");
      setApprovalDialogOpen(false);
      setApprovalComments("");
      fetchApprovalQueue();
    } catch (error) {
      console.error("Error approving travel advance:", error);
      showError("Failed to approve travel advance");
    }
  };

  // Handle rejection
  const handleReject = async () => {
    if (!selectedAdvance) return;

    try {
      await travelAPI.rejectTravelAdvance(selectedAdvance.id, {
        rejection_reason: rejectionReason,
      });
      showSuccess("Travel advance rejected successfully");
      setRejectionDialogOpen(false);
      setRejectionReason("");
      fetchApprovalQueue();
    } catch (error) {
      console.error("Error rejecting travel advance:", error);
      showError("Failed to reject travel advance");
    }
  };

  // Get pending counts based on user role
  const getPendingCounts = () => {
    const counts = {
      pending: 0,
      department_head_approved: 0,
      finance_manager_approved: 0,
    };

    travelAdvances.forEach((advance) => {
      if (
        advance.approval_status === "draft" ||
        advance.approval_status === "pending" ||
        advance.approval_status === "submitted"
      ) {
        counts.pending++;
      } else if (advance.approval_status === "approved by supervisor" || advance.approval_status === "department_head_approved") {
        counts.department_head_approved++;
      } else if (advance.approval_status === "finance_manager_approved") {
        counts.finance_manager_approved++;
      }
    });

    return counts;
  };

  // Table columns
  const travelAdvanceColumns = [
    {
      field: "advance_number",
      headerName: "Advance #",
      type: "text",
      valueGetter: (row) => row.advance_number || row.request_number || `#${row.id}`,
      width: 150,
    },
    {
      field: "user",
      headerName: "Requested By",
      type: "text",
      valueGetter: (row) => `${row.user?.first_name} ${row.user?.sur_name}`,
      width: 200,
    },
    {
      field: "departure_date",
      headerName: "Departure Date",
      type: "date",
      valueGetter: (row) =>
        format(new Date(row.departure_date), "MMM dd, yyyy"),
      width: 150,
    },
    {
      field: "return_date",
      headerName: "Return Date",
      type: "date",
      valueGetter: (row) => format(new Date(row.return_date), "MMM dd, yyyy"),
      width: 150,
    },
    {
      field: "total_cost",
      headerName: "Advance amount (from request)",
      type: "currency",
      valueGetter: (row) =>
        row.total_cost != null
          ? parseFloat(row.total_cost).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) + " TZS"
          : "—",
      width: 200,
    },
    {
      field: "approval_status",
      headerName: "Status",
      type: "status",
      valueGetter: (row) => {
        const status = row.approval_status;
        const statusConfig = travelAdvanceStatuses.find(
          (s) => s.value === status
        );
        return {
          label: statusConfig?.label || status,
          color: statusConfig?.color || "#4F78AE",
          isRejected: status === "rejected",
          rejectionReason: row.rejection_reason,
        };
      },
      renderCell: (params) => {
        const { label, color, isRejected, rejectionReason } = params.value;
        return (
          <Chip
            label={label}
            size="small"
            sx={{
              backgroundColor: color,
              color: "#ffffff",
              fontSize: "0.75rem",
              fontWeight: 500,
              cursor: isRejected ? "help" : "default",
            }}
            title={
              isRejected && rejectionReason
                ? `Rejected: ${rejectionReason}`
                : label
            }
          />
        );
      },
      width: 180,
    },
    {
      field: "payment_status",
      headerName: "Payment",
      type: "string",
      valueGetter: (row) => row.payment_status || (row.approval_status === "approved by finance" ? "pending_payment" : null),
      renderCell: (params) => {
        const status = params.value;
        if (!status) return "—";
        const labels = { pending_payment: "Pending payment", paid: "Paid" };
        const colors = { pending_payment: "#ff9800", paid: "#2e7d32" };
        return (
          <Chip
            label={labels[status] || status}
            size="small"
            sx={{
              backgroundColor: colors[status] || "#9e9e9e",
              color: "#fff",
              fontSize: "0.75rem",
            }}
          />
        );
      },
      width: 140,
    },
    {
      field: "created_at",
      headerName: "Requested On",
      type: "date",
      valueGetter: (row) => format(new Date(row.created_at), "MMM dd, yyyy"),
      width: 150,
    },
  ];

  // Action buttons
  const actionButtons = [
    {
      label: "Approve",
      icon: <ApprovedIcon />,
      color: "success",
      onClick: (advance) => {
        setSelectedAdvance(advance);
        setApprovalDialogOpen(true);
      },
      show: (advance) => {
        // Anyone who can view the queue can see Approve; backend enforces who can actually approve (dept head / Admin)
        if (!canViewQueue) return false;
        const isDraftOrPending =
          advance.approval_status === "draft" ||
          advance.approval_status === "pending" ||
          advance.approval_status === "submitted";
        if (isDraftOrPending) return true;
        // After dept head approves: status is "approved by supervisor" -> show Approve to anyone with queue access (Admin/Finance); backend enforces
        const isWaitingForFinalApproval =
          advance.approval_status === "department_head_approved" || advance.approval_status === "approved by supervisor";
        if (isWaitingForFinalApproval) return true;
        if (hasPermission(PERMISSIONS.ADMIN_APPROVE_TRAVEL_ADVANCES) && advance.approval_status === "finance_manager_approved") return true;
        return false;
      },
    },
    {
      label: "Reject",
      icon: <RejectedIcon />,
      color: "error",
      onClick: (advance) => {
        setSelectedAdvance(advance);
        setRejectionDialogOpen(true);
      },
      show: (advance) => {
        if (!canApprove) return false;

        // Can reject if not already rejected and not fully approved
        return (
          advance.approval_status !== "rejected" &&
          advance.approval_status !== "approved by finance" &&
          advance.approval_status !== "admin_approved"
        );
      },
    },
  ];

  // Effects
  useEffect(() => {
    if (canViewQueue) {
      fetchApprovalQueue();
    } else {
      setLoading(false);
    }
  }, [canViewQueue]);

  // Permission check first: no permission => show message (don't block on loading)
  if (!canViewQueue) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You don't have permission to view the travel advance approval queue.
        </Alert>
      </Box>
    );
  }

  // Loading state (only when user has permission and data is loading)
  if (loading) {
    return <LoadingSpinner />;
  }

  const pendingCounts = getPendingCounts();

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: "bold" }}>
        Travel Advance Approvals
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending Approval
              </Typography>
              <Typography variant="h4" color="warning.main">
                {pendingCounts.pending}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Dept Head Approved
              </Typography>
              <Typography variant="h4" color="info.main">
                {pendingCounts.department_head_approved}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Finance Approved
              </Typography>
              <Typography variant="h4" color="success.main">
                {pendingCounts.finance_manager_approved}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Requests
              </Typography>
              <Typography variant="h4" color="primary.main">
                {travelAdvances.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Empty state when no advances */}
      {!loading && travelAdvances.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body1" fontWeight="medium" gutterBottom>
            No travel advances pending approval
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Travel advances are created when a travel request is approved (a draft advance is added automatically) or when an employee creates one from an approved request under Travel → Travel Advances → Create. Advances from your department will appear here for approval.
          </Typography>
        </Alert>
      )}

      {/* Data Table */}
      <EnhancedDataTable
        data={travelAdvances}
        columns={travelAdvanceColumns}
        loading={loading}
        selectable={false}
        onView={(row) => {
          setSelectedAdvance(row);
          setViewDialogOpen(true);
        }}
        actionButtons={actionButtons}
      />

      {/* View Details Dialog - full details and Approve/Reject when applicable */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Travel Advance Details
          <IconButton
            onClick={() => setViewDialogOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedAdvance ? (
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Advance information
              </Typography>
              <List dense disablePadding>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemText primary="Advance #" secondary={selectedAdvance.advance_number || selectedAdvance.request_number || `#${selectedAdvance.id}`} />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemText
                    primary="Requested by"
                    secondary={`${selectedAdvance.user?.first_name || ""} ${selectedAdvance.user?.sur_name || ""}`.trim() || "—"}
                  />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemText
                    primary="Departure"
                    secondary={selectedAdvance.departure_date ? format(new Date(selectedAdvance.departure_date), "dd MMM yyyy") : "—"}
                  />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemText
                    primary="Return"
                    secondary={selectedAdvance.return_date ? format(new Date(selectedAdvance.return_date), "dd MMM yyyy") : "—"}
                  />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemText
                    primary="Advance amount (from travel request)"
                    secondary={
                      selectedAdvance.total_cost != null
                        ? parseFloat(selectedAdvance.total_cost).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " TZS"
                        : "—"
                    }
                  />
                </ListItem>
                {(selectedAdvance.flatRate || selectedAdvance.flat_rate) && (
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemText
                      primary="Flat rate"
                      secondary={(() => {
                        const fr = selectedAdvance.flatRate || selectedAdvance.flat_rate;
                        return `${fr?.location_category || ""} – ${fr?.amount != null ? parseFloat(fr.amount).toLocaleString() : ""} ${fr?.currency || "TZS"}`;
                      })()}
                    />
                  </ListItem>
                )}
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemText
                    primary="Status"
                    secondary={
                      travelAdvanceStatuses.find((s) => s.value === selectedAdvance.approval_status)?.label || selectedAdvance.approval_status
                    }
                  />
                </ListItem>
                {selectedAdvance.rejection_reason && (
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemText primary="Rejection reason" secondary={selectedAdvance.rejection_reason} />
                  </ListItem>
                )}
              </List>
              <Divider sx={{ my: 2 }} />
              {canViewQueue &&
                selectedAdvance.approval_status !== "rejected" &&
                selectedAdvance.approval_status !== "approved by finance" &&
                selectedAdvance.approval_status !== "admin_approved" && (
                  <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<ApprovedIcon />}
                      onClick={() => {
                        setViewDialogOpen(false);
                        setSelectedAdvance(selectedAdvance);
                        setApprovalDialogOpen(true);
                      }}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<RejectedIcon />}
                      onClick={() => {
                        setViewDialogOpen(false);
                        setSelectedAdvance(selectedAdvance);
                        setRejectionDialogOpen(true);
                      }}
                    >
                      Reject
                    </Button>
                  </Box>
                )}
            </Box>
          ) : (
            <Typography>Loading...</Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog
        open={approvalDialogOpen}
        onClose={() => setApprovalDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Approve Travel Advance</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Comments (Optional)"
            value={approvalComments}
            onChange={(e) => setApprovalComments(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleApprove} variant="contained" color="success">
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog
        open={rejectionDialogOpen}
        onClose={() => setRejectionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Travel Advance</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason *"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            required
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectionDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={!rejectionReason.trim()}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TravelAdvanceApprovalPage;
